import { ReactNode, RefObject, useCallback, useMemo } from 'react';
import { SystemCssProperties } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { Stack } from '@mui/material';

import { Weekday } from '@occc/graphql';
import { Box } from '@occc-app/design-system';

import { CalendarGridDayItem } from './calendar-grid-day-item.component';
import { mergeOverlappingItemsByTimeLane, getItemDurationMinutes } from './utils';
import { CalendarGridDayHeader } from './calendar-grid-day-header.component';
import { CalendarGridDayInfo } from './calendar-grid-day-info.component';

import { CALENDAR_GRID_DEFAULTS } from '../../constants';
import {
  CalendarGridColumn,
  CalendarGridColumnProps,
} from '../calendar-grid-column/calendar-grid-column.component';
import {
  CalendarGridDayMode,
  CalendarGridDayOverlappedTimeLineItem,
  CalendarGridDayTimeLineItem,
  CalendarGridTimeMode,
  OnOverlappedTimeLineItemProps,
} from '../../types';
import { CalendarGridTimeColumn } from '../calendar-grid-time-column/calendar-grid-time-column.component';
import { getCalendarGridColors, normaliseSx } from '../../utils';

const TIME_LANES_PER_DAY = 2;
const HOURS_IN_DAY = 24;
const SHORT_ITEM_THRESHOLD_MINUTES = 120;

export type CalendarGridDayColumnProps = Omit<
  CalendarGridColumnProps,
  'cellCount' | 'renderCellContent'
> & {
  day?: Weekday;
  mode?: CalendarGridDayMode;
  items?: CalendarGridDayTimeLineItem[];
  timeMode?: CalendarGridTimeMode;
  showTimeColumn?: boolean;
  workingAreaRef?: RefObject<HTMLDivElement>;
  onNextDayClick?: () => void;
  onPrevDayClick?: () => void;
  onClick?: (item: CalendarGridDayOverlappedTimeLineItem) => void;
  onOverlappedProps?: OnOverlappedTimeLineItemProps;
  renderAction?: (item: CalendarGridDayTimeLineItem, textColor?: string) => ReactNode;
};

export const CalendarGridDayColumn = ({
  day = Weekday.Sunday,
  mode = 'normal',
  items,
  timeMode = '12H',
  minWidth,
  showTimeColumn,
  onNextDayClick,
  onPrevDayClick,
  onClick,
  onOverlappedProps,
  renderAction,
  workingAreaRef,
  gridStep = CALENDAR_GRID_DEFAULTS.gridStep,
  gridColor = CALENDAR_GRID_DEFAULTS.gridColor,
  gridWidth = CALENDAR_GRID_DEFAULTS.gridWidth,
  sx,
  ...rest
}: CalendarGridDayColumnProps) => {
  const { timeLanesItems, timeLanesCount, maxOverlapping, timeLaneIndexMap } = useMemo(() => {
    const timeLanesItems = mergeOverlappingItemsByTimeLane(
      items || [],
      mode === 'detailed',
      onOverlappedProps,
    );

    const uniqueIndices = [...new Set(items?.map((v) => v.timeLaneIndex) || [])].sort(
      (a, b) => a - b,
    );
    const timeLaneIndexMap = new Map(uniqueIndices.map((idx, i) => [idx, i]));

    return {
      timeLanesItems,
      timeLanesCount: uniqueIndices.length,
      maxOverlapping: Math.max(0, ...timeLanesItems.map((v) => v.overlappedItems?.length || 0)),
      timeLaneIndexMap,
    };
  }, [items, mode, onOverlappedProps]);

  const containerMinWidth = useMemo(() => {
    const containerMinWidth = minWidth || CALENDAR_GRID_DEFAULTS.dayColumnMinWidth;
    const timeLaneWidth = containerMinWidth / 2;

    if (mode === 'normal' && timeLanesCount > TIME_LANES_PER_DAY) {
      return containerMinWidth + (timeLanesCount - TIME_LANES_PER_DAY) * timeLaneWidth;
    }
    if (mode === 'detailed' && maxOverlapping > TIME_LANES_PER_DAY) {
      return containerMinWidth + (maxOverlapping - TIME_LANES_PER_DAY) * timeLaneWidth;
    }

    return containerMinWidth;
  }, [minWidth, mode, timeLanesCount, maxOverlapping]);

  const colors = getCalendarGridColors(gridColor);

  const renderItem = useCallback(
    (
      item: CalendarGridDayOverlappedTimeLineItem,
      timeLaneRatio: number,
      overrideTimeLaneIndex?: number,
    ) => {
      const colorsCount = CALENDAR_GRID_DEFAULTS.dayItemColors.length;
      const colorIndex = item.timeLaneIndex % colorsCount;
      const color = CALENDAR_GRID_DEFAULTS.dayItemColors[colorIndex];
      const textColor = CALENDAR_GRID_DEFAULTS.dayItemTextColors[colorIndex];

      const normalizedIndex =
        overrideTimeLaneIndex !== undefined
          ? overrideTimeLaneIndex
          : timeLaneIndexMap.get(item.timeLaneIndex) ?? 0;

      const hasOverlappedItems = !!item.overlappedItems?.length;
      const isShortItem =
        getItemDurationMinutes(item.startTime, item.endTime) <= SHORT_ITEM_THRESHOLD_MINUTES;
      const showInfo = hasOverlappedItems || isShortItem;

      return (
        <CalendarGridDayItem
          {...item}
          key={`${item.timeLaneIndex}-${item.id}`}
          gridStep={gridStep}
          gridWidth={gridWidth}
          color={color}
          textColor={textColor}
          timeLaneRatio={timeLaneRatio}
          timeLaneIndex={normalizedIndex}
          workingAreaRef={workingAreaRef}
          onClick={onClick ? () => onClick(item) : undefined}
          sx={onClick ? { cursor: 'pointer' } : undefined}
          renderAction={renderAction}
          renderContent={
            showInfo
              ? (anchors, pointerOffset, maxWidth, maxHeight) => (
                  <CalendarGridDayInfo
                    item={item}
                    onClick={onClick}
                    timeMode={timeMode}
                    renderAction={renderAction}
                    arrowHorizontalPosition={anchors.horizontal}
                    arrowVerticalPosition={anchors.vertical}
                    arrowOffset={pointerOffset}
                    sx={{ maxWidth, maxHeight }}
                  />
                )
              : undefined
          }
        />
      );
    },
    [timeLaneIndexMap, gridStep, gridWidth, workingAreaRef, onClick, renderAction, timeMode],
  );

  const column = (
    <CalendarGridColumn
      sx={showTimeColumn ? null : normaliseSx(sx)}
      gridWidth={gridWidth}
      renderHeader={
        showTimeColumn
          ? null
          : (textColor) => (
              <CalendarGridDayHeader
                day={day}
                onNextDayClick={onNextDayClick}
                onPrevDayClick={onPrevDayClick}
                textColor={textColor}
              />
            )
      }
      minWidth={containerMinWidth}
      cellCount={HOURS_IN_DAY}
      gridStep={gridStep}
      {...rest}
    >
      {timeLanesItems.map((item) => {
        if (mode === 'normal' || !item.overlappedItems?.length) {
          return renderItem(item, 1 / (timeLanesCount || 1));
        }
        return item.overlappedItems.map((subitem, index, list) =>
          renderItem(subitem, 1 / list.length, index),
        );
      })}
    </CalendarGridColumn>
  );

  return showTimeColumn ? (
    <Stack sx={[containerStyle, ...normaliseSx(sx)]}>
      <CalendarGridTimeColumn
        mode={timeMode}
        gridWidth={gridWidth}
        borderRight={gridWidth}
        renderHeader={() => null}
      />
      <Stack sx={wrapperStyle}>
        <Box height={gridStep} sx={[headerStyle, { backgroundColor: colors.tintColor }]}>
          <CalendarGridDayHeader
            day={day}
            onNextDayClick={onNextDayClick}
            onPrevDayClick={onPrevDayClick}
            textColor={colors.textColor}
          />
        </Box>
        <Stack sx={contentStyle}>{column}</Stack>
      </Stack>
    </Stack>
  ) : (
    column
  );
};

const containerStyle: SystemCssProperties = {
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flexWrap: 'nowrap',
};

const wrapperStyle: SystemCssProperties = {
  flex: 1,
  overflow: 'hidden',
};

const headerStyle: SystemCssProperties = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'nowrap',
};

const contentStyle: SystemCssProperties = {
  ...containerStyle,
  flex: 1,
  overflowX: 'scroll',
};
